import { bulkResultSchema, type BulkResult, type BulkTarget } from '@forecastify/shared';
import type { SessionState } from '../session';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Send a selection, and get back what actually happened to each row.        │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THIS REPLACES N REQUESTS IN A LOOP, WHICH IS WHAT EVERY BULK ACTION IN THIS PRODUCT WAS.**
 * `logs-route.tsx` carried the loop and the comment explaining it — *"there is no bulk endpoint, so
 * this is N requests"* — with an honest count of the ones that returned ok. The honesty was right
 * and is kept; what was wrong is forty round trips over one decision, and a screen that had to
 * re-derive *"which ones failed"* from a tally.
 *
 * ⚠️⚠️ **THE VERSION TRAVELS, AND IT IS THE POINT.** A bulk edit acts on rows somebody has been
 * looking at — a page loaded minutes ago — so a row that has moved underneath is the ordinary case
 * rather than the edge. Sending the id alone would overwrite a colleague's change on every row it
 * touched, silently. `bulk.ts` in `@forecastify/shared` argues it at length.
 */

/** ⚠️ Every entity in this product carries `version` — CLAUDE.md §3 makes it implied on the table. */
export function bulkTargets(
  rows: readonly { id: string; version: number }[],
  ids: readonly string[],
): BulkTarget[] {
  const chosen = new Set(ids);
  return rows
    .filter((row) => chosen.has(row.id))
    .map((row) => ({ id: row.id, version: row.version }));
}

/**
 * Post a bulk action and parse the per-row result.
 *
 * ⚠️ **`null` on anything that is not a well-formed result**, which the caller renders as
 * *"unavailable"* — `state-rules.md`'s error rule. A half-parsed result reporting three of forty
 * would be worse than saying nothing, because somebody would believe it.
 */
export async function postBulk(
  session: SessionState,
  path: string,
  body: Record<string, unknown>,
): Promise<BulkResult | null> {
  const response = await session
    .request(path, {
      method: 'POST',
      body: JSON.stringify(body),
    })
    .catch(() => null);
  if (response?.ok !== true) return null;

  const payload = (await response.json()) as { data?: unknown };
  const parsed = bulkResultSchema.safeParse(payload.data);
  return parsed.success ? parsed.data : null;
}
